/**
 * /admin/dashboard — "Ma journée admin" style codevoiture (Phase 30.D).
 *
 * Structure :
 *  - Overline "Bonjour + nom" + titre Playfair "Ma journée"
 *  - Encart rouge "Demandes urgentes" (contact requests + candidatures pending + resets)
 *  - 4 KPIs (users, tests 7j, revenus, alertes pédagogiques)
 *  - Accès rapides grille (Utilisateurs, Voiture, Outils, Secrétariat, Moniteur, Boîte mail)
 *  - Alertes système (candidatures pending, resets pending, audit IA)
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Inbox, UserPlus, KeySquare, Bell, Users, Car, Wrench, Building2, GraduationCap,
  Mail, ArrowRight, Newspaper, ShieldAlert, CreditCard, TrendingUp, MessageCircle, FileText, Clapperboard,
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import api from '../../api';
import MaVCardQuickAccess from '../../components/MaVCardQuickAccess';

export default function AdminDashboard() {
  const { user } = useApp() || {};
  const [stats, setStats] = useState(null);
  const [contactCount, setContactCount] = useState(0);
  const [internalCount, setInternalCount] = useState(0);
  const [audit, setAudit] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [shortsPending, setShortsPending] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Tableau de bord · Admin';
    Promise.all([
      api.getAdminStats().catch(() => null),
      api.adminListContactRequests('new').catch(() => null),
      api.getAdminAuditSummary().catch(() => null),
      api.getAdminAlertsCount().catch(() => null),
      api.internalRequestCounts().catch(() => null),
      api.adminShortsCounts?.().catch(() => null),
    ]).then(([s, cr, a, al, ic, sc]) => {
      setStats(s);
      setContactCount(cr?.total || 0);
      setAudit(a);
      setAlerts(al);
      setInternalCount((ic?.new || 0) + (ic?.in_progress || 0));
      setShortsPending(sc?.pending || 0);
    }).finally(() => setLoading(false));
  }, []);

  const firstName = (user?.name || 'Administrateur').split(' ')[0];
  const urgent = {
    contact: contactCount,
    applications: stats?.pending_applications || 0,
    resets: stats?.pending_reset_requests || 0,
    internal: internalCount,
    shorts: shortsPending,
  };
  const totalUrgent = urgent.contact + urgent.applications + urgent.resets + urgent.internal + urgent.shorts;

  return (
    <div className="max-w-6xl" data-testid="admin-dashboard">
      {/* ─── Header ─── */}
      <div className="mb-6">
        <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-red-500 flex items-center gap-2 mb-2">
          Bonjour {firstName} <span className="text-white/30">·</span> Administration
        </div>
        <h1 className="text-4xl sm:text-5xl font-black text-foreground tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Ma journée
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Vue synthétique de la plateforme : demandes urgentes, activité récente et accès rapides aux back-offices.
        </p>
      </div>

      {/* ─── Demandes urgentes ─── */}
      <div className={`rounded-2xl border-2 p-5 sm:p-6 mb-6 ${
        totalUrgent > 0
          ? 'border-red-500/40 bg-gradient-to-br from-red-500/[0.08] to-red-500/[0.02]'
          : 'border-emerald-500/25 bg-emerald-500/[0.04]'
      }`} data-testid="urgent-panel">
        <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-red-500 mb-3">
          Demandes urgentes du jour
        </div>
        <div className="flex items-baseline gap-3 mb-4">
          <div className={`text-5xl sm:text-6xl font-black ${totalUrgent > 0 ? 'text-red-500' : 'text-emerald-500'}`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }} data-testid="urgent-total">
            {totalUrgent}
          </div>
          <div className="text-foreground/70 text-sm sm:text-base">
            {totalUrgent > 1 ? 'demandes à traiter' : totalUrgent === 1 ? 'demande à traiter' : 'Aucune demande — journée sereine 🎉'}
          </div>
        </div>
        {totalUrgent > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="urgent-grid">
            <UrgentLink to="/admin/contact-requests" icon={Inbox} label="Messages contact" count={urgent.contact} color="#dc2626" testid="urgent-contact" />
            <UrgentLink to="/admin/demandes-internes" icon={MessageCircle} label="Demandes internes" count={urgent.internal} color="#dc2626" testid="urgent-internal" />
            <UrgentLink to="/admin/shorts" icon={Clapperboard} label="Shorts à modérer" count={urgent.shorts} color="#dc2626" testid="urgent-shorts" />
            <UrgentLink to="/admin/teacher-applications" icon={UserPlus} label="Candidatures enseignant" count={urgent.applications} color="#dc2626" testid="urgent-apps" />
            <UrgentLink to="/admin/password-resets" icon={KeySquare} label="Reset mots de passe" count={urgent.resets} color="#dc2626" testid="urgent-resets" />
          </div>
        )}
      </div>

      {/* ─── Ma carte de visite (accès rapide démarchage) ─── */}
      <div className="mb-6">
        <MaVCardQuickAccess compact />
      </div>

      {/* ─── KPIs ─── */}
      <section className="mb-6">
        <h2 className="text-lg sm:text-xl font-black text-foreground mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Activité
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="kpi-grid">
          <Kpi icon={Users} label="Utilisateurs" value={stats?.total_users ?? '—'} color="#1d4ed8" to="/admin/users" testid="kpi-users" />
          <Kpi icon={GraduationCap} label="Enseignants" value={stats?.total_enseignants ?? '—'} color="#7c3aed" to="/admin/users" testid="kpi-teachers" />
          <Kpi icon={TrendingUp} label="Tests 7 j" value={stats?.tests_7d ?? '—'} color="#059669" to="/admin/analytics" testid="kpi-tests" />
          <Kpi icon={CreditCard} label="Revenus" value={stats?.revenue_eur != null ? `${stats.revenue_eur.toFixed(0)} €` : '—'} color="#f59e0b" to="/admin/payments" testid="kpi-revenue" />
        </div>
      </section>

      {/* ─── Alertes système ─── */}
      {(audit?.has_report || (alerts?.needs_attention || 0) > 0) && (
        <section className="mb-6" data-testid="system-alerts">
          <h2 className="text-lg sm:text-xl font-black text-foreground mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Alertes système
          </h2>
          <div className="space-y-2">
            {audit?.has_report && audit.counts?.total_pending > 0 && (
              <AlertRow to="/admin/audit" icon={ShieldAlert} label="Audit IA contenu" desc={`${audit.counts.total_pending} suggestion(s) à traiter`} color="#f59e0b" />
            )}
            {alerts?.critical > 0 && (
              <AlertRow to="/admin/alerts" icon={Bell} label="Alertes pédagogiques critiques" desc={`${alerts.critical} élève(s) en difficulté`} color="#dc2626" />
            )}
            {alerts?.needs_attention > 0 && alerts.critical === 0 && (
              <AlertRow to="/admin/alerts" icon={Bell} label="Élèves à surveiller" desc={`${alerts.needs_attention} nécessite(nt) une attention`} color="#f59e0b" />
            )}
          </div>
        </section>
      )}

      {/* ─── Accès rapides ─── */}
      <section>
        <h2 className="text-lg sm:text-xl font-black text-foreground mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Accès rapides
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="quick-actions">
          <Quick to="/admin/users" icon={Users} label="Utilisateurs" desc="Élèves, enseignants, admins" />
          <Quick to="/secretariat/planning" icon={Building2} label="Secrétariat" desc="Planning global auto-école" />
          <Quick to="/moniteur/planning" icon={GraduationCap} label="Espace enseignant" desc="Ma journée moniteur" />
          <Quick to="/admin/voiture" icon={Car} label="Voiture" desc="Import 103 articles" />
          <Quick to="/admin/tools" icon={Wrench} label="Outils" desc="Seed démo, maintenance" />
          <Quick to="/admin/hero-editions" icon={Newspaper} label="Magazine" desc="Éditions hebdo hero" />
          <Quick to="/admin/email-logs" icon={Mail} label="Boîte mail" desc="Logs & mailing" />
          <Quick to="/admin/analytics" icon={TrendingUp} label="Statistiques" desc="Trafic + inscriptions" />
          <Quick to="/admin/community" icon={MessageCircle} label="Communauté" desc="Modération forum ECSR" />
        </div>
      </section>

      {loading && (
        <div className="mt-6 text-white/40 text-xs text-center" data-testid="dashboard-loading">Chargement des données en arrière-plan…</div>
      )}
    </div>
  );
}

function UrgentLink({ to, icon: Icon, label, count, color, testid }) {
  const highlighted = count > 0;
  return (
    <Link to={to} data-testid={testid} className={`rounded-xl border p-3 transition ${
      highlighted
        ? 'border-red-500/40 bg-red-500/[0.08] hover:bg-red-500/[0.14]'
        : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04] opacity-70'
    }`}>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}22`, color }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-foreground text-sm font-semibold truncate">{label}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{count} en attente</div>
        </div>
        {highlighted && <span className="min-w-[26px] h-6 px-2 rounded-full bg-red-600 text-white text-xs font-black inline-flex items-center justify-center animate-pulse">{count}</span>}
      </div>
    </Link>
  );
}

function Kpi({ icon: Icon, label, value, color, to, testid }) {
  return (
    <Link to={to} data-testid={testid} className="rounded-2xl border border-border bg-card hover:border-[color:var(--color-fg-strong,#fff)] p-4 transition group block">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: `${color}22`, color }}>
        <Icon size={15} />
      </div>
      <div className="text-2xl sm:text-3xl font-black text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mt-1">{label}</div>
    </Link>
  );
}

function AlertRow({ to, icon: Icon, label, desc, color }) {
  return (
    <Link to={to} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card hover:bg-muted transition">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}22`, color }}>
        <Icon size={15} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-foreground text-sm font-semibold">{label}</div>
        <div className="text-muted-foreground text-xs">{desc}</div>
      </div>
      <ArrowRight size={14} className="text-muted-foreground" />
    </Link>
  );
}

function Quick({ to, icon: Icon, label, desc }) {
  return (
    <Link to={to} className="rounded-xl border border-border bg-card hover:border-red-500/40 hover:bg-red-500/[0.03] p-4 transition group flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-red-500/15 text-red-500 flex items-center justify-center flex-shrink-0">
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-foreground text-sm font-black">{label}</div>
        <div className="text-muted-foreground text-xs">{desc}</div>
      </div>
      <ArrowRight size={14} className="text-muted-foreground group-hover:text-red-500 group-hover:translate-x-0.5 transition" />
    </Link>
  );
}
